/** Customer-facing Auto Sourcing errors. Raw HTTP/axios messages (e.g.
 * "Request failed with status code 422") and backend codes never reach the UI. */

export const SEARCH_ERROR_GENERIC =
  "We couldn't start this search. Please check your search settings and try again.";
export const SAVE_ERROR_GENERIC =
  "We couldn't save this search. Please check your search settings and try again.";
export const CATEGORY_UNAVAILABLE_ERROR =
  "This category is no longer available. Choose another category.";
export const CUSTOM_QUERY_REQUIRED_ERROR = "Enter what you want to search for.";
export const RETAILER_UNAVAILABLE_ERROR =
  "This retailer isn't available right now. Choose another retailer.";
export const NETWORK_ERROR = "We couldn't reach Ezisell. Check your connection and try again.";
export const SESSION_ERROR = "Your session has expired. Sign in again to continue.";
export const SCAN_SLOT_EXHAUSTED_ERROR =
  "All scan slots are currently in use. Wait for a scan to finish or open History to review running jobs.";

function detailPayload(err) {
  return err?.response?.data?.detail;
}

function detailText(err) {
  const detail = detailPayload(err);
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const parts = [detail.message, detail.code, detail.reason].filter(
      (v) => typeof v === "string" && v.trim(),
    );
    if (parts.length) return parts.join(" ");
  }
  if (Array.isArray(detail)) {
    return detail
      .map((d) => [d?.msg, Array.isArray(d?.loc) ? d.loc.join(".") : ""].join(" "))
      .join(" ");
  }
  return "";
}

function safeServerMessage(err) {
  const detail = detailPayload(err);
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const msg = typeof detail.message === "string" ? detail.message.trim() : "";
    // Never surface stack traces / internal exception blobs.
    if (msg && msg.length <= 280 && !/traceback|exception|stack/i.test(msg)) {
      return msg;
    }
  }
  return "";
}

/** Human label + friendly sentence for a FastAPI/Pydantic validation error. */
const FIELD_LABELS = {
  "scan_limits.max_products_per_source": "Maximum Products to Discover",
  "scan_limits.max_sources": "Max sources",
  "scan_limits.max_provider_requests": "Provider budget",
  "scan_limits.max_duration_minutes": "Scan duration",
  "scan_limits.products_per_source_mode": "Product limit mode",
  "demand.min_rating": "Minimum rating",
  "demand.min_reviews": "Minimum reviews",
  "demand.min_monthly_sold": "Minimum monthly sold",
  "demand.max_monthly_sold": "Maximum monthly sold",
  "competition.min_seller_count": "Minimum seller count",
  "competition.max_seller_count": "Maximum seller count",
  "profitability.max_acquisition_cost": "Maximum acquisition cost",
  "profitability.min_estimated_roi": "Minimum ROI",
  "profitability.min_estimated_profit": "Minimum profit",
  "schedule.interval_minutes": "Repeat interval",
  "name": "Search name",
};

function fieldKey(loc) {
  if (!Array.isArray(loc)) return "";
  // Drop the leading "body" marker.
  const parts = loc.filter((p) => typeof p === "string" && p !== "body");
  return parts.join(".");
}

/** Turn ONE Pydantic error entry into an actionable sentence, or null. */
function describeValidationError(entry) {
  if (!entry || typeof entry !== "object") return null;
  const field = fieldKey(entry.loc);
  const known = FIELD_LABELS[field] || "";
  const label = known || (field ? field.replace(/[._]/g, " ") : "");
  const type = String(entry.type || "");
  const msg = String(entry.msg || "");
  const ctx = entry.ctx && typeof entry.ctx === "object" ? entry.ctx : {};

  if (type === "greater_than_equal") {
    const min = ctx.ge;
    if (min != null) {
      if (field === "scan_limits.max_duration_minutes") {
        return "Scan duration must be at least 1 minute.";
      }
      return label ? `${label} must be at least ${min}.` : `That value must be at least ${min}.`;
    }
  }
  if (type === "less_than_equal") {
    const max = ctx.le;
    if (max != null) {
      if (field === "scan_limits.max_duration_minutes") {
        return "Maximum continuous scan duration is 72 hours.";
      }
      return label ? `${label} must be ${max} or less.` : `That value must be ${max} or less.`;
    }
  }
  if (type === "missing") {
    return label ? `${label} is required.` : "A required setting is missing.";
  }
  if (type === "literal_error") {
    return known ? `Choose a supported value for ${known.toLowerCase()}.` : null;
  }
  if (type === "value_error") {
    const cleaned = msg.replace(/^value error,\s*/i, "").trim();
    if (!cleaned) return null;
    if (/profile_name_blank/i.test(cleaned)) return "Give this search a name.";
    if (/interval_minutes is required/i.test(cleaned)) {
      return "Recurring schedules need a repeat interval.";
    }
    if (/min_monthly_sold must be </i.test(cleaned)) {
      return "Minimum monthly sold cannot be greater than the maximum.";
    }
    return known ? `${known}: ${cleaned}` : null;
  }
  // Unknown constraint on a field we can name is still worth saying once.
  if (msg && known) {
    const cleaned = msg.replace(/^value error,\s*/i, "").trim();
    return cleaned ? `${known}: ${cleaned}` : null;
  }
  return null;
}

/**
 * Actionable sentence for a request-body validation failure.
 *
 * A FastAPI 422 returns `detail` as an ARRAY of field errors. Without this
 * translation the UI could only fall back to a generic "check your settings",
 * which tells the customer nothing about which setting is wrong.
 */
export function validationErrorMessage(err) {
  const detail = detailPayload(err);
  if (!Array.isArray(detail) || !detail.length) return "";
  const described = detail
    .map((entry) => describeValidationError(entry))
    .filter((text) => typeof text === "string" && text.trim());
  if (!described.length) return "";
  // One clear sentence beats a wall of text; mention extras when present.
  const [first, ...rest] = described;
  if (!rest.length) return first;
  return `${first} Also check: ${rest.join(" ")}`;
}

export function friendlyAutoSourcingError(err, { action = "start" } = {}) {
  const fallback = action === "save" ? SAVE_ERROR_GENERIC : SEARCH_ERROR_GENERIC;
  const status = err?.response?.status;
  if (!err?.response) {
    return err?.request || err?.code === "ERR_NETWORK" || err?.code === "ECONNABORTED"
      ? NETWORK_ERROR
      : fallback;
  }
  if (status === 401) return SESSION_ERROR;

  const detail = detailPayload(err);
  const text = detailText(err).toLowerCase();
  const code =
    detail && typeof detail === "object" && !Array.isArray(detail)
      ? String(detail.code || detail.reason || "").toLowerCase()
      : "";

  if (
    status === 429 ||
    code === "scan_slot_exhausted" ||
    text.includes("scan_slot_exhausted") ||
    text.includes("scan slots are in use")
  ) {
    return safeServerMessage(err) || SCAN_SLOT_EXHAUSTED_ERROR;
  }
  if (text.includes("category_unavailable") || text.includes("category_url")) {
    return CATEGORY_UNAVAILABLE_ERROR;
  }
  if (
    text.includes("custom_category_selection_requires_user_query") ||
    text.includes("user_query")
  ) {
    return CUSTOM_QUERY_REQUIRED_ERROR;
  }
  if (
    text.includes("source_not_operational") ||
    text.includes("source_not_allowlisted") ||
    text.includes("source_not_found") ||
    text.includes("source_excluded") ||
    text.includes("source_disabled") ||
    text.includes("unknown_source") ||
    text.includes("no_operational_sources") ||
    text.includes("at_least_one_source_required")
  ) {
    return RETAILER_UNAVAILABLE_ERROR;
  }
  if (
    text.includes("max_provider_requests_must_be_non_negative") ||
    text.includes("max_provider_requests_invalid")
  ) {
    return "Enter a provider request budget of 0 or more. 0 means direct discovery only.";
  }
  // A request-body validation failure (422) arrives as an array of field errors.
  // Handle it BEFORE the loose text heuristics below: those match on the field
  // path, so they would report a minimum violation as the 72-hour maximum.
  const validation = validationErrorMessage(err);
  if (validation) return validation;

  if (text.includes("max_duration") || text.includes("72 hours") || text.includes("4320")) {
    return "Your scan duration must be 72 hours or less.";
  }
  if (text.includes("no_retailers_selected")) {
    return "Select at least one Ready retailer before starting a search.";
  }

  // Prefer a short, safe structured server message over the generic toast.
  const safe = safeServerMessage(err);
  if (safe) return safe;

  return fallback;
}
